import {
  ChartNoAxesCombined,
  FlaskConical,
  PackageSearch,
  Store,
} from 'lucide-react'
import type { NavContext, NavGroup, NavItem, NavLeaf } from './navConfig.types'

export const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Sources',
    items: [
      {
        label: 'AEO Optimization',
        icon: ChartNoAxesCombined,
        path: '/tracker',
        children: [
          { label: 'History', path: '/tracker/history' },
          { label: 'Schedules', path: '/tracker/schedules' },
          { label: 'Performance', path: '/tracker/performance' },
          {
            label: 'Site Health',
            path: '/tracker/site-health',
            requiresSiteHealth: true,
          },
        ],
      },
      {
        label: 'Products & Knowledge',
        icon: PackageSearch,
        children: [
          { label: 'Products', path: '/products' },
          {
            label: 'Knowledge',
            path: '/knowledge',
            requiredPermission: 'chat_configurations:read',
          },
        ],
      },
    ],
  },
  {
    label: 'Activation',
    items: [
      {
        label: 'Shopping Assistant',
        icon: Store,
        children: [
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Integrations', path: '/integrations' },
          { label: 'Leads', path: '/leads' },
          {
            label: 'Profile Properties',
            path: '/profile-properties',
            requiresCdpConnection: true,
            unlockPopover: true,
          },
        ],
      },
      {
        label: 'Experience Lab',
        icon: FlaskConical,
        children: [{ label: 'Dashboard', path: '/experience-lab' }],
        requiresExperienceLabAccess: true,
      },
    ],
  },
]

export function buildNav(groups: NavGroup[], context: NavContext): NavGroup[] {
  return groups
    .map((group) => ({
      ...group,
      items: group.items
        .map((item) => buildItem(item, context))
        .filter((item): item is NavItem => item !== null),
    }))
    .filter((group) => group.items.length > 0)
}

function buildItem(item: NavItem, context: NavContext): NavItem | null {
  if (item.requiresExperienceLabAccess && !context.experienceLabAllowed) {
    return null
  }
  if (!item.children) return item

  const children = item.children
    .filter((leaf) => isVisible(leaf, context))
    .map((leaf) => withDisabledState(leaf, context))

  if (children.length === 0 && !item.path) return null

  return { ...item, children }
}

function isVisible(leaf: NavLeaf, context: NavContext): boolean {
  if (leaf.requiresExperienceLabAccess && !context.experienceLabAllowed) {
    return false
  }

  if (leaf.requiresSiteHealth && !context.siteHealthEnabled) return false

  if (!leaf.requiredPermission) return true

  if (!context.hasUser) return false

  return context.hasPermission(leaf.requiredPermission)
}

function withDisabledState(leaf: NavLeaf, context: NavContext): NavLeaf {
  const disabled = Boolean(
    leaf.requiresCdpConnection && !context.hasCdpConnection
  )

  return { ...leaf, disabled }
}
